import React from "react";

const primedImageSources = new Set();

function primeImage(src) {
  if (!src || primedImageSources.has(src)) return;

  const img = new Image();
  img.decoding = "async";
  img.src = src;

  const markPrimed = () => {
    primedImageSources.add(src);
  };

  if (typeof img.decode === "function") {
    img.decode().then(markPrimed).catch(markPrimed);
    return;
  }

  img.onload = markPrimed;
  img.onerror = markPrimed;
}

function circularDistance(a, b, total) {
  if (!Number.isFinite(total) || total <= 0) return Math.abs(a - b);
  const diff = Math.abs(a - b);
  return Math.min(diff, total - diff);
}

export default function SlickLazyImage({
  src,
  alt,
  className,
  style,
  index,
  activeIndex,
  total,
  buffer = 2,
  eager = false,
}) {
  const imgRef = React.useRef(null);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const isFirst = index === 0;
  const isEager = Boolean(eager) && isFirst;

  const distance = circularDistance(index ?? 0, activeIndex ?? 0, total);
  const shouldPrioritize = isEager || distance <= buffer;
  const priorityValue = isEager ? "high" : distance <= 1 ? "high" : shouldPrioritize ? "auto" : "low";

  React.useEffect(() => {
    setIsLoaded(false);
  }, [src]);

  React.useEffect(() => {
    if (!imgRef.current) return;
    imgRef.current.setAttribute("fetchpriority", priorityValue);
  }, [priorityValue]);

  React.useEffect(() => {
    if (!shouldPrioritize) return;
    primeImage(src);
  }, [shouldPrioritize, src]);

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={`${className || ""} ${isLoaded ? "slick-lazy-image--loaded" : "slick-lazy-image--loading"}`.trim()}
      style={{
        ...style,
        opacity: isLoaded ? 1 : 0.88,
        transition: "opacity 320ms ease",
      }}
      decoding="async"
      loading={shouldPrioritize ? "eager" : "lazy"}
      onLoad={() => setIsLoaded(true)}
    />
  );
}
